/* eslint-disable react/self-closing-comp */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import gql from 'graphql-tag';
import { ButtonGroup, Col, Form, Row } from 'react-bootstrap';
import { Button, Icon } from '@storable/canaveral-components';
import { MANAGE } from '../../../modules/authorization/actions';
import { BUILDER, SEO_PAGES } from '../../../modules/authorization/subjects';
import { getWebDomain } from '../../../modules/domain';
import useCurrentUser from '../../hooks/useCurrentUser';
import SettingsHeader from '../SettingsHeader';
import {
  editSuccessMessage,
  editWarningMessage,
  generateEditFailureMessage,
  primaryDomainMessage,
  unauthorizedMessage,
} from './messages';
import styles from './styles.scss';

const DEFAULT_CONTENT_TEXT = 'We automatically generate the sitemap and prevent crawlers from indexing admin routes.';

// textarea can't handle null, so we coerce empty input to an empty string
const coerceToString = value => value || '';

const getRobotsLink = (currentHost, identifier, primaryHostname = null) => {
  if (!primaryHostname) {
    return null;
  }

  return getWebDomain(currentHost, { identifier, primaryHostname }, 'robots.txt');
};

const propTypes = {
  onSubmit: PropTypes.func.isRequired,
  site: PropTypes.shape({
    identifier: PropTypes.string.isRequired,
    primaryHostname: PropTypes.shape({
      hostname: PropTypes.string,
      ssl: PropTypes.bool,
    }),
    robots: PropTypes.shape({
      customContent: PropTypes.string,
      defaultContent: PropTypes.string,
    }),
  }).isRequired,
  submitErrors: PropTypes.array,
  submitted: PropTypes.bool,
  submitting: PropTypes.bool,
};

const defaultProps = {
  submitErrors: [],
  submitted: false,
  submitting: false,
};

const RobotsSettings = ({
  onSubmit,
  site: {
    identifier,
    primaryHostname,
    robots: {
      customContent = null,
      defaultContent,
    },
  },
  submitErrors,
  submitted,
  submitting,
}) => {
  const [userContent, setUserContent] = useState(coerceToString(customContent));
  const [dirty, setDirty] = useState(false);
  const [editing, setEditing] = useState(false);

  const onChange = ({ target: { value } }) => {
    const targetValue = coerceToString(value);
    setUserContent(targetValue);
    setDirty(targetValue !== coerceToString(customContent));
  };

  const toggleEdit = () => setEditing(!editing);

  const cancelEdit = () => {
    setUserContent(coerceToString(customContent));
    setDirty(false);
    toggleEdit();
  };

  const saveEdit = () => {
    const content = coerceToString(userContent.trim());
    setUserContent(content);
    onSubmit(content);
    toggleEdit();
  };

  const currentUser = useCurrentUser();
  const authorized = currentUser.can(MANAGE, BUILDER, SEO_PAGES);
  const { location: { host } } = window;
  const robotsLink = getRobotsLink(host, identifier, primaryHostname);

  return (
    <>
      <Row>
        <Col xs={12}>
          {primaryHostname ? editWarningMessage : primaryDomainMessage}
          <SettingsHeader text="robots.txt" />
        </Col>
      </Row>
      <Row>
        <Col xs={12}>
          {primaryHostname && (
            <Form.Text className={cx(styles.helpContainer, styles.linkOut)}>
              <span>{DEFAULT_CONTENT_TEXT}</span>
              <Button
                as="a"
                context="neutral"
                href={robotsLink}
                target="_blank"
              >
                <span>View robots.txt</span>
                <Icon id="external-link" label="view robots.txt file" />
              </Button>
            </Form.Text>
          )}
          <div className={cx(styles.contentContainer, styles.defaultContent)}>
            <textarea disabled value={defaultContent}></textarea>
          </div>
        </Col>
      </Row>
      {primaryHostname && (
        <Row>
          <Col xs={12}>
            {!authorized && unauthorizedMessage}
            <Form.Text className={styles.helpContainer}>
              <span>Add content to your robots.txt file by editing the field below.</span>
              <ButtonGroup>
                {!editing && (
                  <Button
                    variant="neutral"
                    data-testid="edit-robots-content"
                    disabled={!authorized}
                    onClick={toggleEdit}
                  >
                    Edit robots.txt
                  </Button>
                )}
                {editing && (
                  <>
                    <Button variant="neutral" onClick={cancelEdit}>Cancel</Button>
                    <Button
                      context="positive"
                      disabled={!dirty}
                      loading={submitting}
                      onClick={saveEdit}
                      type="submit"
                    >
                      Save
                    </Button>
                  </>
                )}
              </ButtonGroup>
            </Form.Text>
            <div className={cx(styles.contentContainer, styles.customContent)}>
              <textarea disabled={!editing} onChange={onChange} value={userContent}></textarea>
            </div>
          </Col>
        </Row>
      )}
      {submitted && editSuccessMessage}
      {submitErrors.length > 0 && generateEditFailureMessage(submitErrors)}
    </>
  );
};

RobotsSettings.propTypes = propTypes;
RobotsSettings.defaultProps = defaultProps;

RobotsSettings.fragments = {
  robots: gql`
    fragment RobotsSettingsRobots on Robots {
      customContent
      defaultContent
    }
  `,
  site: gql`
    fragment RobotsSettingsSite on Site {
      identifier
      primaryHostname {
        id
        hostname
        ssl
      }
    }
  `,
};

export default RobotsSettings;
